'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import PageHeader from '../../components/shared/PageHeader';
import PageFooter from '../../components/shared/PageFooter';
import { PopupButton as TypeformPopupButton } from '@typeform/embed-react';
import {
  SUPPORT_SECTIONS,
  itemTitle,
  itemArticle,
  getSection,
  searchTopics,
  topicHref,
  sectionHref,
  topicSlug,
  INLINE_ICON_PATTERN,
  INLINE_ACCENT_PATTERN,
  type TopicArticle,
} from './sections';

// The summary (tally) tab's glyph, copied from GlobalBottomNav's inactive
// tally cell: a receipt whose bottom line item is a heart. Drawn at text size
// and in currentColor so it sits in a sentence like a word rather than a
// pasted-in button. The horizontal scale on the receipt body is what gives the
// bottom-bar glyph its proportions — keep it, or the receipt reads too narrow.
//
// Two deliberate departures from the bottom bar's copy: it's 22px rather than
// the bar's 28 (below ~20 the zigzag base mushes into the outline and it stops
// reading as a receipt), and vectorEffect="non-scaling-stroke" is dropped. That
// flag pins the stroke to 1.7 SCREEN px, which the bar wants at 28px but which
// is ~35% too heavy once the glyph shrinks to sit in a paragraph.
const SummaryIcon = () => (
  <svg
    width="22" height="22" viewBox="0 0 24 24" fill="none"
    style={{ verticalAlign: '-5px', margin: '0 2px' }}
    role="img" aria-label="summary"
  >
    <g transform="translate(12 12) scale(1.429, 0.967) translate(-12 -12)">
      <path
        d="M6.8 3H16l3 3v15l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5V4.8A1.8 1.8 0 0 1 6.8 3z"
        stroke="currentColor" strokeWidth="1.7"
        strokeLinejoin="round" strokeLinecap="round" fill="none"
      />
    </g>
    <path
      d="M12 15.94 C7.87 12.19 8.25 9.19 10.12 9.94 A1.875 1.875 0 0 1 12 11.44 A1.875 1.875 0 0 1 13.88 9.94 C15.75 9.19 16.13 12.19 12 15.94 Z"
      fill="currentColor"
    />
  </svg>
);

const INLINE_ICON_COMPONENTS: Record<string, () => React.JSX.Element> = {
  'summary-icon': SummaryIcon,
};

// The app's accent ramp (mint → cyan → lavender), clipped to the glyphs.
const OMBRE_TEXT: React.CSSProperties = {
  backgroundImage: 'linear-gradient(90deg, #6ee7b7 0%, #06dffa 45%, #dcccfe 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: 'transparent',
  // Gradient text reads thin against the dark page at the body weight.
  fontWeight: 600,
};

// An @-mention, or a bare "@" being talked about. The leading group is what
// keeps email addresses out: `support@tabster.ai` has a word character before
// the @, so it never matches. (Written as a prefix group rather than a
// lookbehind — lookbehind needs Safari 16.4+, and this ships in an iOS
// WebView that may be older.)
const MENTION_PATTERN = '(^|[^\\w.@])(@\\w*)';

// Split a body paragraph on {{icon}} tokens and @-mentions, swapping in the
// real glyph / the accent styling. Copy with neither comes back as-is.
// Replace every match of `pattern` inside the plain-string runs of `nodes`,
// leaving already-rendered elements alone. `skip` is how many leading
// characters of the match are context rather than content (the @-mention
// pattern captures the character before the @ so it can rule out emails).
const replaceInNodes = (
  nodes: React.ReactNode[],
  pattern: string,
  render: (match: RegExpExecArray, key: string) => React.ReactNode,
  skip: (match: RegExpExecArray) => number = () => 0,
): React.ReactNode[] =>
  nodes.flatMap((node, nodeIndex) => {
    if (typeof node !== 'string') return [node];
    const re = new RegExp(pattern, 'g');
    const out: React.ReactNode[] = [];
    let cursor = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(node)) !== null) {
      const start = m.index + skip(m);
      if (start > cursor) out.push(node.slice(cursor, start));
      const rendered = render(m, `${nodeIndex}-${start}`);
      if (rendered !== null) out.push(rendered);
      cursor = m.index + m[0].length;
    }
    if (cursor < node.length) out.push(node.slice(cursor));
    return out.length ? out : [node];
  });

// Turn a body paragraph into nodes: {{icon}} tokens become real glyphs,
// [[text]] takes the accent ombre, and @-mentions are highlighted. Copy with
// none of those comes back as the original string.
const renderBodyCopy = (para: string): React.ReactNode => {
  let nodes: React.ReactNode[] = [para];

  // Icon tokens. An unknown name is dropped rather than printing raw braces.
  nodes = replaceInNodes(nodes, INLINE_ICON_PATTERN, (m, key) => {
    const Icon = INLINE_ICON_COMPONENTS[m[1]];
    return Icon ? <Icon key={`icon-${key}`} /> : null;
  });

  // [[Accented lead-ins]] and [[link text|/href]].
  nodes = replaceInNodes(nodes, INLINE_ACCENT_PATTERN, (m, key) => (
    m[2]
      ? (
        <a
          key={`link-${key}`}
          href={m[2]}
          style={{ ...OMBRE_TEXT, textDecoration: 'underline', textUnderlineOffset: '2px' }}
        >
          {m[1]}
        </a>
      )
      : <span key={`accent-${key}`} style={OMBRE_TEXT}>{m[1]}</span>
  ));

  // @-mentions. skip() steps past the captured preceding character so it
  // stays in the surrounding text rather than inside the highlight.
  nodes = replaceInNodes(
    nodes,
    MENTION_PATTERN,
    (m, key) => <span key={`mention-${key}`} style={OMBRE_TEXT}>{m[2]}</span>,
    (m) => m[1].length,
  );

  return nodes.length === 1 && typeof nodes[0] === 'string' ? nodes[0] : nodes;
};

// Screenshots are phone-shot portrait, same as the clips — they share the
// video's sizing class so a still and a clip sit at identical width in the
// column. width/height reserve the box before decode so copy below doesn't
// jump (CLS).
const ArticleImage = ({ src, alt, width, height }: {
  src: string; alt?: string; width?: number; height?: number;
}) => (
  <img
    className="support-article-video"
    src={src}
    alt={alt || ''}
    width={width}
    height={height}
    loading="lazy"
    decoding="async"
    style={width && height ? { aspectRatio: `${width} / ${height}` } : undefined}
  />
);

// Article video. Autoplays, but only once it's actually on screen.
//
// Plain `autoplay` would undo the page-load work: it fetches the file on every
// visit whether or not anyone watches, which is 1-3MB per article. Gating on
// IntersectionObserver keeps first paint at just the poster (~55KB) and pulls
// the video only when someone scrolls to it — and pauses again when they
// scroll away, so a backgrounded article isn't burning data or battery.
//
// `muted` is not optional: every browser blocks autoplay with sound. These are
// screen recordings whose audio is near-silent (mean -39 dB), so nothing is
// lost — but `controls` stays so anyone can unmute and scrub.
const ArticleVideo = ({ article }: { article: TopicArticle }) => {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Someone who's asked for less motion gets the poster and a play button.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // play() rejects when the browser refuses (iOS Low Power Mode, data
          // saver, or a muted-autoplay policy we don't satisfy). That's a fine
          // outcome — the poster and controls are still there — so swallow it
          // rather than throwing an unhandled rejection into the console.
          el.play().catch(() => {});
        } else if (!el.paused) {
          el.pause();
        }
      },
      // Half the player visible: enough that it's genuinely being looked at,
      // and it won't trigger from a fast scroll past.
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [article.video]);

  // Media fragment (#t=20 or #t=20,45): another article can link straight to a
  // moment in this clip. Seek there, scroll it into view, and — when an end is
  // given — stop at it, so "watch :20s to :45s" plays exactly that.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    const m = /[#&]t=([\d.]+)(?:,([\d.]+))?/.exec(window.location.hash);
    if (!m) return;
    const start = parseFloat(m[1]);
    const end = m[2] ? parseFloat(m[2]) : null;

    // Assigning currentTime is silently dropped while the element has nothing
    // seekable yet, and readyState >= 1 does NOT imply it does. That's how the
    // old fast path failed: on a warm HTTP cache readyState passes 1 before
    // this effect runs, so it seeked against a not-yet-seekable element and the
    // {once:true} listener meant there was never a second attempt — the clip
    // played from 0 and the "watch :23s to :38s" link pointed at nothing.
    // Retry on each event that can widen the seekable range instead.
    const RETRY_ON = ['loadedmetadata', 'loadeddata', 'canplay', 'progress'];

    const seek = () => {
      const ranges = el.seekable;
      if (!ranges.length || ranges.end(ranges.length - 1) < start) return;
      try { el.currentTime = start; } catch { return; }
      RETRY_ON.forEach((e) => el.removeEventListener(e, seek));
      el.scrollIntoView({ block: 'center' });
      el.play().catch(() => {});
    };

    RETRY_ON.forEach((e) => el.addEventListener(e, seek));
    // preload is "none", so nudge it to fetch enough to seek.
    if (el.readyState < 1) {
      el.preload = 'metadata';
      el.load();
    }
    seek();

    const cleanup = () => RETRY_ON.forEach((e) => el.removeEventListener(e, seek));
    if (end === null) return cleanup;
    // `loop` would otherwise carry it past the excerpt.
    const onTime = () => { if (el.currentTime >= end) el.pause(); };
    el.addEventListener('timeupdate', onTime);
    return () => {
      cleanup();
      el.removeEventListener('timeupdate', onTime);
    };
  }, [article.video]);

  return (
    <video
      ref={ref}
      className="support-article-video"
      src={article.video}
      poster={article.poster}
      width={article.videoWidth}
      height={article.videoHeight}
      controls
      playsInline
      muted
      loop
      // 'none' (not 'metadata'): with a poster there's nothing to gain from
      // fetching the file's header on every view — the bytes only load when
      // the observer above starts playback, or the user hits play.
      preload="none"
      controlsList="nodownload"
      style={article.videoWidth && article.videoHeight
        ? { aspectRatio: `${article.videoWidth} / ${article.videoHeight}` }
        : undefined}
    />
  );
};

// A topic is one subheading within a section — its own page.
type Topic = { sectionId: string; itemIndex: number };

// What the content column is showing. The standalone web routes drive this
// from the URL (see `initialView`); the in-app embedded modal drives it from
// state alone.
export type View =
  | { kind: 'home' }
  | { kind: 'category'; sectionId: string }
  | { kind: 'topic'; sectionId: string; itemIndex: number };

// Search box + results dropdown. Self-contained query state so the desktop
// (sidebar) and mobile (content top) instances don't fight — only one is
// visible at a time.
const SupportSearch = ({ onOpenTopic }: { onOpenTopic: (t: Topic) => void }) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const results = focused && query.trim() ? searchTopics(query).slice(0, 12) : [];
  const showEmpty = focused && !!query.trim() && !results.length;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
      >
        <circle cx="11" cy="11" r="7" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
        <path d="M20 20l-3.5-3.5" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        type="search"
        className="support-search-input"
        placeholder="Search help topics"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        // Delay the close so a result's onMouseDown still fires first.
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        aria-label="Search help topics"
      />
      {(results.length > 0 || showEmpty) && (
        <div className="support-search-results">
          {results.map((r) => {
            const sec = getSection(r.sectionId)!;
            return (
              <button
                key={`${r.sectionId}--${topicSlug(r.item)}`}
                type="button"
                className="support-search-result"
                // onMouseDown beats the input's onBlur, so the dropdown is
                // still mounted when the click lands.
                onMouseDown={(e) => {
                  e.preventDefault();
                  setQuery('');
                  onOpenTopic(r);
                }}
              >
                <span style={{ flexShrink: 0 }} aria-hidden="true">{sec.emoji}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', color: 'rgba(255,255,255,0.9)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.item}
                  </span>
                  <span style={{ display: 'block', color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginTop: '1px' }}>
                    {sec.short}
                  </span>
                </span>
              </button>
            );
          })}
          {showEmpty && (
            <div style={{ padding: '12px 14px', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
              No topics match &ldquo;{query.trim()}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Scroll-triggered fade-in hook (matching about page)
// Help-center view state that has to outlive a remount.
//
// Each /support view is a SEPARATE route (/support, /support/<section>,
// /support/<section>/<topic>), so moving between them unmounts one page
// component and mounts another — React state doesn't survive it. That's what
// made every navigation replay the entrance animations (reading as a load) and
// collapse whichever category you'd opened.
//
// Module scope, deliberately not sessionStorage: this is transient view state,
// not something to restore on a fresh visit. It also keeps hydration honest —
// the sets are empty on first render, matching the server HTML, and only fill
// once the user has interacted.
const supportViewState = {
  expandedCards: new Set<string>(),
  expandedSections: new Set<string>(),
  // Regions that have already played their entrance fade this session.
  faded: new Set<string>(),
};

// Fades a region in on first sight — once per session. Re-running it on every
// route change is what made going back to the index look like it was loading.
const useFadeIn = (key: string) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(() => supportViewState.faded.has(key));
  useEffect(() => {
    if (isVisible) { supportViewState.faded.add(key); return; }
    const el = ref.current;
    if (!el) return;
    // threshold 0 (not 0.1): the category list is several thousand px tall,
    // so "10% of the element visible" sits right at the edge of firing on a
    // phone — the whole section would stay at opacity 0.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          supportViewState.faded.add(key);
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible, key]);
  return { ref, style: {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
    // No transition once it's already been seen — otherwise coming back to the
    // index would animate from the restored state and reintroduce the flicker.
    transition: supportViewState.faded.has(key) && isVisible
      ? 'none'
      : 'opacity 0.7s ease, transform 0.7s ease',
  } as React.CSSProperties };
};

// "← Browse other topics" crumb at the top of a drilled-in page.
// The label takes the app's cyan ombre, so it reads as a link rather than the
// muted grey it used to be — it is the only way back up from an article, and at
// 55% white it was the quietest thing on the page. The arrow and any emoji stay
// outside the clipped span: background-clip:text on an emoji renders it as a
// flat gradient silhouette.
const BackLink = ({ emoji, label, onClick }: { emoji?: string; label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="support-backlink"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: 'transparent',
      border: 'none',
      padding: 0,
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: 'inherit',
    }}
  >
    <span aria-hidden="true" style={{ color: '#06dffa' }}>←</span>
    {emoji && <span aria-hidden="true">{emoji}</span>}
    <span style={OMBRE_TEXT}>{label}</span>
  </button>
);

const GradientDivider = () => (
  <div style={{
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(168,139,250,0.3) 30%, rgba(168,139,250,0.5) 50%, rgba(168,139,250,0.3) 70%, transparent 100%)',
    margin: '48px 0',
  }} />
);

const SupportContent = ({
  embedded = false,
  initialView,
}: {
  embedded?: boolean;
  // Set by the server routes (/support/<section>/<topic>) so the first paint
  // — including the server-rendered HTML a crawler sees — is the article
  // itself, not the index.
  initialView?: View;
}) => {
  // Three levels: the home index (category cards), a category page (its list
  // of topics), and a topic page (one subheading's article).
  const [view, setView] = useState<View>(initialView || { kind: 'home' });
  const router = useRouter();
  // Sidebar accordion — every category starts collapsed; clicking its
  // heading opens that category page and reveals its topics.
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set([
      ...supportViewState.expandedSections,
      ...(initialView && initialView.kind !== 'home' ? [initialView.sectionId] : []),
    ])
  );
  // Home-index accordion — narrow layouts only. Seeded from the module store so
  // a category you opened is still open when you come back from an article.
  // Empty on a genuinely fresh load, which is what keeps the server HTML and
  // the first client paint in agreement.
  const [expandedCards, setExpandedCards] = useState<Set<string>>(
    () => new Set(supportViewState.expandedCards)
  );

  // Mirror both accordions into the module store on every change, so the
  // remount that a route change causes can pick them back up.
  useEffect(() => { supportViewState.expandedCards = new Set(expandedCards); }, [expandedCards]);
  useEffect(() => { supportViewState.expandedSections = new Set(expandedSections); }, [expandedSections]);
  // Below the 900px breakpoint there's no sidebar, so drilling into a
  // category page just to read a list is a wasted tap — expand in place
  // instead. The in-app modal is always this layout.
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (embedded) { setIsNarrow(true); return; }
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 899px)');
    const sync = () => setIsNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [embedded]);

  const layoutRef = useRef<HTMLDivElement>(null);
  const font = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';

  const sectionsFade = useFadeIn('sections');
  const contactFade = useFadeIn('contact');

  // Bring the new page into view ONLY when the content column has already
  // scrolled off the top — otherwise navigating from a resting page would
  // yank the hero away and read as the page jumping down.
  const scrollContentTop = useCallback(() => {
    const el = layoutRef.current;
    if (!el) return;
    if (el.getBoundingClientRect().top >= 0) return;
    el.scrollIntoView({ block: 'start' });
  }, []);

  const expandSection = useCallback((id: string) => {
    setExpandedSections((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // Navigate. On the web each view is a REAL route (/support,
  // /support/<section>, /support/<section>/<topic>) so every article has its
  // own prerendered HTML, title, and canonical URL. The view flips
  // immediately and the router catches up, so the click still feels instant.
  // The embedded (in-app modal) copy has no router to drive — it stays pure
  // state so opening Help never rewrites the app's URL.
  const go = useCallback((next: View) => {
    setView(next);
    if (next.kind !== 'home') expandSection(next.sectionId);
    if (!embedded) {
      let href = '/support';
      if (next.kind === 'category') {
        href = sectionHref(next.sectionId);
      } else if (next.kind === 'topic') {
        const sec = getSection(next.sectionId);
        const item = sec?.items[next.itemIndex];
        if (sec && item !== undefined) href = topicHref(sec.id, item);
      }
      router.push(href);
    }
    scrollContentTop();
  }, [embedded, expandSection, scrollContentTop, router]);

  // Sidebar heading: opens the category page. Clicking the one you're already
  // reading collapses it again, so the accordion still toggles.
  const onCategoryClick = useCallback((sectionId: string) => {
    if (view.kind === 'category' && view.sectionId === sectionId && expandedSections.has(sectionId)) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });
      return;
    }
    go({ kind: 'category', sectionId });
  }, [view, expandedSections, go]);

  // Home-index card. Narrow: expand the topics in place (no sidebar to fall
  // back on). Wide: open the category page as before.
  const onCategoryCardClick = useCallback((sectionId: string) => {
    if (!isNarrow) {
      go({ kind: 'category', sectionId });
      return;
    }
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId); else next.add(sectionId);
      return next;
    });
  }, [isNarrow, go]);

  // Route → state. Next reuses this component instance across sibling routes,
  // so a back/forward hop swaps `initialView` without re-running useState's
  // initializer; without this the URL would move and the view would not.
  const initialViewKey = initialView ? JSON.stringify(initialView) : '';
  useEffect(() => {
    if (embedded || !initialView) return;
    setView(initialView);
    if (initialView.kind !== 'home') expandSection(initialView.sectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialViewKey, embedded]);

  // Back-compat: the help center briefly used ?section= / ?topic= query
  // params. Anything already shared or indexed under those redirects to the
  // real route (replace, so it doesn't add a history entry).
  useEffect(() => {
    if (embedded || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const legacyTopic = params.get('topic');
    const legacySection = params.get('section');
    if (!legacyTopic && !legacySection) return;
    if (legacyTopic) {
      const [secId, ...rest] = legacyTopic.split('--');
      const sec = getSection(secId);
      const slug = rest.join('--');
      const idx = sec ? sec.items.findIndex((it) => topicSlug(it) === slug) : -1;
      if (sec && idx >= 0) {
        router.replace(topicHref(sec.id, sec.items[idx]));
        return;
      }
    }
    if (legacySection && getSection(legacySection)) {
      router.replace(sectionHref(legacySection));
      return;
    }
    router.replace('/support');
  }, [embedded, router]);

  const viewSection = view.kind === 'home' ? null : getSection(view.sectionId) || null;
  const viewTopicItem = view.kind === 'topic' && viewSection
    ? viewSection.items[view.itemIndex]
    : null;
  const viewTopicTitle = viewTopicItem != null ? itemTitle(viewTopicItem) : null;
  const viewTopicArticle = viewTopicItem != null ? itemArticle(viewTopicItem) : null;

  // Exactly one <h1> per view, and it names what the page is actually about.
  // On the index that's the help center itself; on a category or an article the
  // hero drops to a <p> and the category/article title takes the h1.
  const HeroTag = view.kind === 'home' ? 'h1' : 'p';
  const TopicTitleTag = 'h1';
  const SectionTitleTag = view.kind === 'category' ? 'h1' : 'h2';

  return (
    <>
      <style>{`
        @keyframes cardEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Step card hover */
        .support-step-card {
          transition: background 0.3s ease, border-color 0.3s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease;
        }
        .support-step-card:hover {
          background: rgba(255,255,255,0.06) !important;
          border-color: rgba(168,139,250,0.2) !important;
          transform: translateY(-3px);
          box-shadow: 0 8px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(168,139,250,0.12);
        }

        /* Article video — portrait screen recordings. Fills the column on
           phones; capped on desktop so it doesn't dwarf the copy. */
        .support-article-video {
          display: block;
          /* height:auto + the inline aspect-ratio reserve the box before
             metadata loads, so the poster/player never shifts the copy
             beneath it (CLS). */
          width: 100%;
          height: auto;
          max-width: 100%;
          /* 14px top (not 4) so a screenshot doesn't crowd the copy it
             illustrates — with the paragraph's 8px bottom that's a 22px gap,
             matching the space given BELOW an image before the next step.
             Also separates two screenshots shown back to back. */
          /* 22px bottom (not 8) — the drop shadow below now needs room to fall
             before the next paragraph starts, or the copy sits inside it. */
          margin: 14px 0 22px;
          border-radius: 20px;
          background: #000;
          /* Dark screenshots on a dark page have almost no edge of their own,
             so elevation has to come from a hairline plus layered shadow rather
             than shadow alone. Three depths: a tight contact shadow to seat it,
             a mid ambient one, and a wide soft one for the lift. The lavender
             ring is the same accent the step cards use on hover, so the two
             elevated surfaces on this page read as one system. */
          border: 1px solid rgba(255,255,255,0.12);
          box-shadow:
            0 1px 2px rgba(0,0,0,0.40),
            0 8px 20px rgba(0,0,0,0.45),
            0 24px 56px rgba(0,0,0,0.42),
            0 0 0 1px rgba(168,139,250,0.10);
          object-fit: contain;
        }
        @media (min-width: 620px) {
          .support-article-video {
            width: auto;
            max-height: 70vh;
          }
        }

        /* Share feedback — rectangular at the same 10px radius the app uses for
           its square-ish controls (UploadFlow's Share Link, the payment-method
           selector), so it reads as the same family rather than a new shape.
           Typeform's PopupButton renders a bare <button>, so everything
           including the reset has to be declared here. */
        .support-feedback-btn {
          display: inline-block;
          margin-top: 18px;
          padding: 12px 22px;
          border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.18);
          background: rgba(255,255,255,0.04);
          color: white;
          font-size: 14.5px;
          font-weight: 700;
          letter-spacing: -0.01em;
          font-family: inherit;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .support-feedback-btn:hover {
          background: rgba(255,255,255,0.08);
          border-color: rgba(168,139,250,0.35);
        }

        /* Home index — one card per category. */
        .support-category-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 14px;
        }
        @media (min-width: 620px) {
          .support-category-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        /* Modal-embedded help is a narrow column — keep one card per row. */
        .support-embedded .support-category-grid {
          grid-template-columns: 1fr !important;
        }
        .support-category-card {
          display: block;
          width: 100%;
          padding: 24px;
          text-align: left;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          font-family: inherit;
          -webkit-tap-highlight-color: transparent;
          /* A card that expands shouldn't stretch its grid row-mates. */
          align-self: start;
        }
        /* The tappable header inside the card (the card itself can't be a
           button — it now contains buttons). */
        .support-category-head {
          display: block;
          width: 100%;
          padding: 0;
          margin: 0;
          background: transparent;
          border: none;
          text-align: left;
          cursor: pointer;
          font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        /* Emoji column. A FIXED width (rather than letting the glyph size
           itself) is what lets the topic lists below indent to exactly where
           the heading text starts — emoji advance widths vary per glyph and
           per platform, so measuring them would never line up. */
        .support-emoji {
          display: inline-block;
          width: var(--support-emoji-col, 22px);
          flex-shrink: 0;
        }
        /* Topic lists start where the heading text does. */
        .support-topic-list {
          padding-left: calc(var(--support-emoji-col, 22px) + 12px);
        }

        /* Topic row inside an expanded home card. */
        .support-card-topic {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          width: 100%;
          padding: 11px 0;
          background: transparent;
          border: none;
          border-top: 1px solid rgba(255,255,255,0.07);
          color: rgba(255,255,255,0.75);
          font-size: 15px;
          font-family: inherit;
          line-height: 1.45;
          text-align: left;
          cursor: pointer;
          transition: color 0.15s ease;
          -webkit-tap-highlight-color: transparent;
        }
        .support-card-topic:hover {
          color: white;
        }

        /* Topic rows on a category page — each opens its own page. */
        .support-topic-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          width: 100%;
          padding: 16px 20px;
          font-size: 15px;
          font-weight: 500;
          color: rgba(255,255,255,0.8);
          line-height: 1.45;
          text-align: left;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 14px;
          cursor: pointer;
          font-family: inherit;
          transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
          -webkit-tap-highlight-color: transparent;
        }
        .support-topic-card:hover {
          background: rgba(255,255,255,0.07);
          border-color: rgba(168,139,250,0.25);
          color: white;
          transform: translateX(2px);
        }
        .support-topic-card-chevron {
          font-size: 18px;
          line-height: 1;
          color: rgba(255,255,255,0.35);
          flex-shrink: 0;
        }
        .support-topic-card:hover .support-topic-card-chevron {
          color: rgba(255,255,255,0.7);
        }

        /* Contact card */
        .support-contact-card {
          transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.2s ease, box-shadow 0.3s ease, border-color 0.2s ease;
        }
        .support-contact-card:hover {
          transform: translateY(-2px);
          background: rgba(255,255,255,0.08) !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.2), 0 0 0 1px rgba(168,139,250,0.15);
          border-color: rgba(168,139,250,0.25) !important;
        }

        /* Search */
        .support-search-input {
          width: 100%;
          box-sizing: border-box;
          padding: 10px 14px 10px 38px;
          border-radius: 12px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          color: white;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s ease, background 0.2s ease;
          -webkit-appearance: none;
          appearance: none;
        }
        .support-search-input::placeholder {
          color: rgba(255,255,255,0.35);
        }
        .support-search-input:focus {
          border-color: rgba(6,223,250,0.5);
          background: rgba(255,255,255,0.08);
        }
        .support-search-results {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          z-index: 60;
          background: #11162b;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 14px;
          box-shadow: 0 16px 40px rgba(0,0,0,0.5);
          max-height: 320px;
          overflow-y: auto;
          padding: 6px;
        }
        .support-search-result {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 9px 10px;
          border: none;
          border-radius: 10px;
          background: transparent;
          color: white;
          font-size: 13px;
          font-family: inherit;
          text-align: left;
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .support-search-result:hover {
          background: rgba(255,255,255,0.08);
        }

        /* Layout */
        .support-layout {
          max-width: 720px;
          margin: 0 auto;
          padding: 0 24px;
        }
        .support-sidebar {
          display: none;
        }
        .support-content {
          width: 100%;
        }
        /* Mobile search sits above the cards; the sidebar copy takes over on
           desktop. */
        .support-search-mobile {
          display: block;
          margin-bottom: 24px;
        }

        /* Partiful-style nav rows: rounded pill highlights, no left rail. */
        .support-sidebar-link {
          display: block;
          padding: 7px 12px;
          margin: 2px 8px;
          font-size: 12px;
          font-weight: 500;
          color: rgba(255,255,255,0.55);
          text-decoration: none;
          border-radius: 10px;
          transition: color 0.2s ease, background 0.2s ease;
          line-height: 1.45;
          cursor: pointer;
          /* Repeated toggling shouldn't leave text selected behind the pill. */
          user-select: none;
          -webkit-user-select: none;
        }
        .support-sidebar-link:hover {
          color: white;
        }
        .support-sidebar-link.category {
          padding: 9px 12px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.85);
        }
        /* The open category gets Partiful's soft pill treatment. */
        .support-sidebar-link.category.expanded {
          background: rgba(255,255,255,0.08);
          color: white;
        }
        .support-sidebar-link.category.active {
          color: white;
        }
        .support-sidebar-link.indent {
          padding: 8px 14px 8px 18px;
        }
        /* Selected topic — rounded highlight with a white edge bar, like
           Partiful's selected article row. */
        .support-sidebar-link.indent.active {
          position: relative;
          overflow: hidden;
          background: rgba(255,255,255,0.14);
          color: white;
          font-weight: 600;
        }
        .support-sidebar-link.indent.active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: white;
        }
        .support-sidebar-chevron {
          font-size: 15px;
          line-height: 1;
          color: rgba(255,255,255,0.5);
          transition: transform 0.25s ease;
          flex-shrink: 0;
        }
        .support-sidebar-link.category.expanded .support-sidebar-chevron {
          transform: rotate(90deg);
        }

        @media (min-width: 900px) {
          .support-layout {
            max-width: 1100px !important;
            padding: 0 48px !important;
            display: flex !important;
            gap: 56px !important;
            align-items: flex-start !important;
          }
          /* Desktop only: sit the title over the sidebar's search box instead
             of centring it across the whole 1100px shell, where it floated
             above the gap between the two columns and belonged to neither.
             The hero wrap shares the layout's 48px padding, so its content
             edge already matches the sidebar; the extra 16px matches the
             sidebar's own inner padding and lines the title up with the search
             input itself. !important because the centring is an inline style.
             max-width keeps a longer title from running past the column. */
          .support-hero-title {
            text-align: left !important;
            padding-left: 16px !important;
            max-width: 250px !important;
          }
          .support-sidebar {
            display: block !important;
            position: sticky !important;
            top: 24px !important;
            width: 250px !important;
            min-width: 250px !important;
            padding-top: 8px !important;
            max-height: calc(100vh - 48px) !important;
            overflow-y: auto !important;
          }
          .support-content {
            flex: 1 !important;
            min-width: 0 !important;
          }
          .support-search-mobile {
            display: none !important;
          }
          .page-floating-cta {
            display: none !important;
          }
        }

        /* When embedded in a modal, force mobile layout regardless of viewport */
        .support-embedded .support-layout {
          max-width: 100% !important;
          padding: 0 24px !important;
          display: block !important;
        }
        .support-embedded .support-sidebar {
          display: none !important;
        }
        .support-embedded .support-search-mobile {
          display: block !important;
        }
        .support-embedded .support-hero-wrap {
          max-width: 100% !important;
          padding: 0 24px !important;
        }
        /* The desktop rule above hangs the title over the sidebar's search box.
           Embedded (the in-app Help modal) renders the mobile layout with no
           sidebar at all, but it can still be wider than 900px on a tablet — so
           it would pick up that rule and left-align against nothing. Higher
           specificity, so it wins wherever it lands in the sheet. */
        .support-embedded .support-hero-title {
          text-align: center !important;
          padding-left: 0 !important;
          max-width: 100% !important;
        }
        .support-embedded .support-content {
          padding-bottom: 120px !important;
        }
      `}</style>

      <div className={embedded ? 'support-embedded' : ''} style={{
        ...(!embedded ? { minHeight: '100vh' } : {}),
        background: 'linear-gradient(180deg, #0a0f32 0%, #0a1230 8%, #0c1a35 18%, #0e2240 30%, #102a48 45%, #0e2240 60%, #0c1a35 75%, #0a1230 88%, #0a0f32 100%)',
        fontFamily: font,
        color: 'white',
      }}>
        {/* showSettingsGear=false: the floating bottom-left gear reads as a
            stray/misplaced control on this standalone support route (it's the
            logged-in "back to settings" affordance meant for marketing pages).
            The in-app Help view already skips PageHeader entirely (embedded). */}
        {!embedded && <PageHeader showSettingsGear={false} />}

        {/* Hero */}
        <div
          className="support-hero-wrap"
          style={{
            maxWidth: '1100px',
            margin: '0 auto',
            padding: '0 48px',
          }}
        >
          {/* Styled to match the Tips-page hero (32px Bricolage, lavender
              gradient, 36px buffers).

              It is only an <h1> on the index. It used to be one on every
              view, so all ~40 article pages shipped the same "Tabster Help
              Center" as their top-level heading while the actual subject sat
              in an <h2> — telling a crawler that every page in the help
              center is about the same thing. On an article or a category the
              hero renders as a plain <p> (identical styling) and the heading
              rank moves down to the title that describes the page. */}
          <HeroTag className="support-hero-title" style={{
            fontSize: '32px',
            fontWeight: '600',
            marginTop: '36px',
            marginBottom: '36px',
            lineHeight: '1.3',
            textAlign: 'center',
            letterSpacing: '-0.02em',
            fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
            background: 'linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}>
            Tabster Help Center
          </HeroTag>
        </div>

        {/* Main layout: sidebar + content */}
        <div className="support-layout" ref={layoutRef}>
          {/* Sidebar: search on top, then every category with its topic pages
              nested beneath it. */}
          <nav className="support-sidebar">
            <div style={{ padding: '0 16px 16px' }}>
              <SupportSearch onOpenTopic={(t) => go({ kind: 'topic', ...t })} />
            </div>
            {SUPPORT_SECTIONS.map((sec) => {
              const isExpanded = expandedSections.has(sec.id);
              const isCategoryActive = view.kind === 'category' && view.sectionId === sec.id;
              return (
                <div key={sec.id} style={{ marginBottom: '4px' }}>
                  <a
                    className={`support-sidebar-link category ${isExpanded ? 'expanded' : ''} ${isCategoryActive ? 'active' : ''}`}
                    onClick={() => onCategoryClick(sec.id)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                    aria-expanded={isExpanded}
                  >
                    <span>
                      <span style={{ marginRight: '8px' }} aria-hidden="true">{sec.emoji}</span>
                      {sec.short}
                    </span>
                    <span className="support-sidebar-chevron" aria-hidden="true">
                      ›
                    </span>
                  </a>
                  {isExpanded && sec.items.map((item, i) => {
                    const isActive = view.kind === 'topic'
                      && view.sectionId === sec.id
                      && view.itemIndex === i;
                    return (
                      <a
                        key={i}
                        className={`support-sidebar-link indent ${isActive ? 'active' : ''}`}
                        onClick={() => go({ kind: 'topic', sectionId: sec.id, itemIndex: i })}
                      >
                        {itemTitle(item)}
                      </a>
                    );
                  })}
                </div>
              );
            })}
            <a
              className="support-sidebar-link category"
              href="mailto:support@tabster.ai"
              style={{ marginTop: '6px' }}
            >
              <span style={{ marginRight: '8px' }} aria-hidden="true">✉️</span>
              Contact
            </a>
          </nav>

          {/* Content */}
          <div className="support-content">

            {/* Mobile / embedded search (the sidebar copy is desktop-only) */}
            <div className="support-search-mobile">
              <SupportSearch onOpenTopic={(t) => go({ kind: 'topic', ...t })} />
            </div>

            {view.kind === 'topic' && viewSection && viewTopicTitle ? (
              /* Topic page — placeholder until the guide content is written. */
              <div>
                {/* Articles return to the index, not to their category —
                    the category list is one tap away from there anyway, and
                    "Browse other topics" is the same target from every article. */}
                <BackLink label="Browse other topics" onClick={() => go({ kind: 'home' })} />
                {/* The page's h1 — see the note on HeroTag. Heading rank is
                    independent of type scale, so this stays at 16px. */}
                <TopicTitleTag style={{
                  margin: '0 0 14px',
                  fontSize: '16px',
                  fontWeight: '800',
                  color: 'white',
                  letterSpacing: '-0.01em',
                  lineHeight: '1.35',
                  fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
                }}>
                  {viewTopicTitle}
                </TopicTitleTag>
                {viewTopicArticle?.body?.length || viewTopicArticle?.sections?.length || viewTopicArticle?.blocks?.length || viewTopicArticle?.video || viewTopicArticle?.image ? (
                  <>
                    {viewTopicArticle.body?.map((para, i) => (
                      <p key={i} style={{
                        margin: '0 0 8px',
                        fontSize: '15px',
                        color: 'rgba(255,255,255,0.7)',
                        lineHeight: '1.6',
                      }}>
                        {renderBodyCopy(para)}
                      </p>
                    ))}
                    {viewTopicArticle.sections?.map((sec) => (
                      <section key={sec.heading} style={{ margin: '0 0 22px' }}>
                        <h3 style={{
                          margin: '18px 0 8px',
                          fontSize: '15px',
                          letterSpacing: '-0.01em',
                          fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
                          // Shared with @-mentions so the two accents can't drift.
                          ...OMBRE_TEXT,
                          // Heavier than the mention weight OMBRE_TEXT carries.
                          fontWeight: 800,
                          // An inline gradient box would stretch the full column
                          // width; shrink it to the text so the ramp reads
                          // across the words themselves.
                          display: 'inline-block',
                        }}>
                          {sec.heading}
                        </h3>
                        {/* Prose sections — a heading over plain paragraphs,
                            for groups that aren't a numbered sequence. */}
                        {sec.body?.map((para, i) => (
                          <p key={i} style={{
                            margin: '0 0 8px',
                            fontSize: '15px',
                            color: 'rgba(255,255,255,0.7)',
                            lineHeight: '1.6',
                          }}>
                            {renderBodyCopy(para)}
                          </p>
                        ))}
                        {/* A real <ol> so the numbering is semantic (and stays
                            correct for screen readers and Google) rather than
                            typed into the copy. */}
                        {sec.steps?.length ? (
                        <ol style={{
                          margin: 0,
                          paddingLeft: '22px',
                          fontSize: '15px',
                          color: 'rgba(255,255,255,0.7)',
                          lineHeight: '1.6',
                        }}>
                          {sec.steps.map((step, i) => (
                            <li key={i} style={{ margin: '0 0 10px' }}>
                              {(Array.isArray(step) ? step : [step]).map((para, j) => (
                                <p key={j} style={{ margin: j === 0 ? 0 : '8px 0 0' }}>
                                  {renderBodyCopy(para)}
                                </p>
                              ))}
                            </li>
                          ))}
                        </ol>
                        ) : null}
                      </section>
                    ))}
                    {/* Interleaved copy + screenshots, in the order authored. */}
                    {viewTopicArticle.blocks?.map((block, i, all) => (
                      block.kind === 'text' ? (
                        <p key={i} style={{
                          // Copy that follows a screenshot needs real
                          // separation — at the paragraph's usual 8px it read
                          // as a caption hanging off the image above rather
                          // than the start of the next step.
                          margin: all[i - 1]?.kind === 'image' ? '22px 0 8px' : '0 0 8px',
                          fontSize: '15px',
                          color: 'rgba(255,255,255,0.7)',
                          lineHeight: '1.6',
                        }}>
                          {renderBodyCopy(block.text)}
                        </p>
                      ) : (
                        <ArticleImage
                          key={i}
                          src={block.src}
                          alt={block.alt}
                          width={block.width}
                          height={block.height}
                        />
                      )
                    ))}
                    {viewTopicArticle.image && (
                      <ArticleImage
                        src={viewTopicArticle.image}
                        alt={viewTopicArticle.imageAlt}
                        width={viewTopicArticle.imageWidth}
                        height={viewTopicArticle.imageHeight}
                      />
                    )}
                    {viewTopicArticle.video && (
                      /* Phone-shot screen recordings are portrait — cap the
                         height so the video doesn't tower over the copy on
                         desktop, and let it fill the column on mobile. */
                      <ArticleVideo article={viewTopicArticle} />
                    )}
                  </>
                ) : (
                  <div
                    className="support-step-card"
                    style={{
                      padding: '28px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '20px',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                    }}
                  >
                    <p style={{
                      margin: 0,
                      fontSize: '15px',
                      color: 'rgba(255,255,255,0.6)',
                      lineHeight: '1.8',
                    }}>
                      This guide is on its way. We&apos;re writing it now. In the
                      meantime, email{' '}
                      <a
                        href="mailto:support@tabster.ai"
                        style={{
                          background: 'linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          color: 'transparent',
                          textDecoration: 'none',
                          fontWeight: '700',
                        }}
                      >
                        support@tabster.ai
                      </a>{' '}
                      with any questions about {viewTopicTitle.toLowerCase().replace(/!$/, '')} and
                      we&apos;ll walk you through it.
                    </p>
                  </div>
                )}
              </div>
            ) : view.kind === 'category' && viewSection ? (
              /* Category page — the section's topics, each its own page. */
              <div>
                <BackLink label="Browse other topics" onClick={() => go({ kind: 'home' })} />
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '24px' }}>
                  {/* Emoji matches the heading's own font size. */}
                  <span className="support-emoji" style={{ fontSize: '18px', lineHeight: '1.3' }} aria-hidden="true">
                    {viewSection.emoji}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <SectionTitleTag style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '800',
                      color: 'white',
                      letterSpacing: '-0.01em',
                      lineHeight: '1.3',
                      fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
                    }}>
                      {viewSection.title}
                    </SectionTitleTag>
                    {viewSection.tagline && (
                      <p style={{
                        margin: '4px 0 0',
                        fontSize: '15px',
                        color: 'rgba(255,255,255,0.45)',
                        lineHeight: '1.5',
                      }}>
                        {viewSection.tagline}
                      </p>
                    )}
                  </div>
                </div>
                <div className="support-topic-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {viewSection.items.map((item, i) => (
                    <button
                      key={i}
                      type="button"
                      className="support-topic-card"
                      onClick={() => go({ kind: 'topic', sectionId: viewSection.id, itemIndex: i })}
                    >
                      <span style={{ minWidth: 0 }}>{itemTitle(item)}</span>
                      <span className="support-topic-card-chevron" aria-hidden="true">›</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Home index — one card per category. On phones (and in the
                 in-app modal) a card expands its topics in place; on desktop,
                 where the sidebar already lists everything, it opens the
                 category page. */
              <div ref={sectionsFade.ref} style={sectionsFade.style}>
                <div className="support-category-grid">
                  {SUPPORT_SECTIONS.map((section, index) => {
                    const isCardOpen = expandedCards.has(section.id);
                    return (
                      <div
                        key={section.id}
                        id={section.id}
                        className="support-step-card support-category-card"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <button
                          type="button"
                          className="support-category-head"
                          aria-expanded={isCardOpen}
                          onClick={() => onCategoryCardClick(section.id)}
                        >
                          {/* Emoji sits beside the title, not stacked above
                              it, so the card reads as one line of heading. */}
                          <span style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            {/* Emoji matches the card heading's font size. */}
                            <span className="support-emoji" style={{ fontSize: '17px', lineHeight: '1.35' }} aria-hidden="true">
                              {section.emoji}
                            </span>
                            <span style={{ minWidth: 0 }}>
                              <span style={{
                                display: 'block',
                                fontSize: '17px',
                                fontWeight: '700',
                                color: 'white',
                                letterSpacing: '-0.01em',
                                lineHeight: '1.35',
                              }}>
                                {section.title}
                              </span>
                              {section.tagline && (
                                <span style={{
                                  display: 'block',
                                  marginTop: '4px',
                                  fontSize: '14px',
                                  color: 'rgba(255,255,255,0.45)',
                                  lineHeight: '1.5',
                                }}>
                                  {section.tagline}
                                </span>
                              )}
                              <span style={{
                                display: 'block',
                                marginTop: '10px',
                                fontSize: '13px',
                                fontWeight: '600',
                                backgroundImage: 'linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                color: 'transparent',
                              }}>
                                {section.items.length} article{section.items.length === 1 ? '' : 's'}
                                <span
                                  aria-hidden="true"
                                  style={{
                                    display: 'inline-block',
                                    marginLeft: '4px',
                                    // Points right when closed, down when open —
                                    // only ever open on the narrow layout.
                                    transform: isCardOpen ? 'rotate(90deg)' : 'none',
                                    transition: 'transform 0.2s ease',
                                  }}
                                >
                                  ›
                                </span>
                              </span>
                            </span>
                          </span>
                        </button>
                        {isCardOpen && (
                          <ul className="support-topic-list" style={{ listStyle: 'none', margin: '10px 0 0' }}>
                            {section.items.map((item, i) => (
                              <li key={i}>
                                <button
                                  type="button"
                                  className="support-card-topic"
                                  onClick={() => go({ kind: 'topic', sectionId: section.id, itemIndex: i })}
                                >
                                  <span style={{ minWidth: 0 }}>{itemTitle(item)}</span>
                                  <span className="support-topic-card-chevron" aria-hidden="true">›</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <GradientDivider />

            {/* Contact */}
            <div
              ref={contactFade.ref}
              id="contact"
              style={{
                marginBottom: '64px',
                ...contactFade.style,
              }}
            >
              {/* No card around this any more — the copy sits directly on the
                  page. The bubble was competing with the article's own elevated
                  screenshots for attention at the foot of every page. */}
              <div className="support-contact-card">
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: 'white',
                  marginTop: 0,
                  marginBottom: '10px',
                  letterSpacing: '-0.01em',
                  fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
                }}>
                  Need more help?
                </h2>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.8',
                }}>
                  We&apos;re here for you. Email us at{' '}
                  <a
                    href="mailto:support@tabster.ai"
                    style={{
                      background: 'linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      color: 'transparent',
                      textDecoration: 'none',
                      fontWeight: '700',
                    }}
                  >
                    support@tabster.ai
                  </a>{' '}
                  and we&apos;ll get back to you as soon as possible.
                </p>
                {/* Same Typeform (id XMDJWlcA) the settings modal opens, so
                    feedback from the help centre lands in the same place.
                    PopupButton renders its own <button>, hence the class rather
                    than inline styles. */}
                <TypeformPopupButton id="XMDJWlcA" className="support-feedback-btn">
                  Share feedback
                </TypeformPopupButton>
              </div>
            </div>

          </div>
        </div>

        {!embedded && <PageFooter />}
      </div>
    </>
  );
};

export default SupportContent;
